import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FEATURE_ICON_NAMES, FeatureIcon } from "@/components/FeatureIcon";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyFeature = { title: "", description: "", icon: "sparkles", display_order: 0 };

export default function AdminFeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState(emptyFeature);
  const [adding, setAdding] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/admin/features");
      setFeatures(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load features");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (id, field, value) => {
    setFeatures((current) => current.map((feature) => (feature.id === id ? { ...feature, [field]: value } : feature)));
  };

  const addFeature = async (event) => {
    event.preventDefault();
    if (!newFeature.title.trim()) {
      toast.error("Feature title is required");
      return;
    }
    setAdding(true);
    try {
      const response = await api.post("/admin/features", { ...newFeature, display_order: Number(newFeature.display_order) || 0 });
      setFeatures((current) => [...current, response.data]);
      setNewFeature(emptyFeature);
      toast.success("Feature added");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not add feature");
    } finally {
      setAdding(false);
    }
  };

  const saveFeature = async (feature) => {
    setSavingId(feature.id);
    try {
      const response = await api.patch(`/admin/features/${feature.id}`, {
        title: feature.title,
        description: feature.description,
        icon: feature.icon,
        display_order: Number(feature.display_order) || 0,
        is_active: Boolean(feature.is_active),
      });
      setFeatures((current) => current.map((item) => (item.id === feature.id ? response.data : item)));
      toast.success("Feature saved");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not save feature");
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (feature) => {
    updateRow(feature.id, "is_active", !feature.is_active);
    try {
      await api.patch(`/admin/features/${feature.id}`, { is_active: !feature.is_active });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not update feature");
      load();
    }
  };

  const deleteFeature = async (feature) => {
    if (!window.confirm(`Delete "${feature.title}"? This removes it from the public features section.`)) return;
    try {
      await api.delete(`/admin/features/${feature.id}`);
      setFeatures((current) => current.filter((item) => item.id !== feature.id));
      toast.success("Feature deleted");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not delete feature");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="features-management-page">
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Link to="/admin" data-testid="features-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-blue-300 transition-colors hover:text-blue-200">
          <ArrowLeft className="h-4 w-4" /> Back to admin dashboard
        </Link>
        <div className="mb-6">
          <h1 className="font-headline text-3xl font-bold text-white">Features Management</h1>
          <p className="mt-1 text-sm text-slate-400">Active features appear automatically on the customer catalog, ordered by display order.</p>
        </div>
        <form onSubmit={addFeature} className="mb-8 rounded-2xl border border-slate-800 bg-slate-900/80 p-5" data-testid="add-feature-form">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Add new feature</h2>
          <div className="grid gap-3 md:grid-cols-[1fr_2fr_140px_110px_auto]">
            <Input data-testid="new-feature-title-input" placeholder="Title" value={newFeature.title} onChange={(event) => setNewFeature({ ...newFeature, title: event.target.value })} className="border-slate-700 bg-slate-950 text-slate-100" />
            <Input data-testid="new-feature-description-input" placeholder="Description" value={newFeature.description} onChange={(event) => setNewFeature({ ...newFeature, description: event.target.value })} className="border-slate-700 bg-slate-950 text-slate-100" />
            <select data-testid="new-feature-icon-select" value={newFeature.icon} onChange={(event) => setNewFeature({ ...newFeature, icon: event.target.value })} className="h-9 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
              {FEATURE_ICON_NAMES.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <Input data-testid="new-feature-order-input" type="number" placeholder="Order" value={newFeature.display_order} onChange={(event) => setNewFeature({ ...newFeature, display_order: event.target.value })} className="border-slate-700 bg-slate-950 text-slate-100" />
            <Button data-testid="add-feature-button" type="submit" disabled={adding} className="bg-blue-500 text-white hover:bg-blue-400">
              <Plus className="h-4 w-4" /> {adding ? "Adding..." : "Add"}
            </Button>
          </div>
        </form>
        <div className="space-y-3" data-testid="features-list">
          {features.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/60 p-8 text-center text-sm text-slate-500" data-testid="features-empty-state">
              No features yet. Add your first feature above.
            </div>
          ) : (
            features.map((feature) => (
              <div key={feature.id} className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4" data-testid={`feature-row-${feature.id}`}>
                <div className="grid items-center gap-3 lg:grid-cols-[44px_1fr_2fr_140px_100px_90px_auto_auto]">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-300">
                    <FeatureIcon name={feature.icon} className="h-5 w-5" />
                  </div>
                  <Input data-testid={`feature-title-input-${feature.id}`} value={feature.title} onChange={(event) => updateRow(feature.id, "title", event.target.value)} className="border-slate-700 bg-slate-950 text-slate-100" />
                  <Input data-testid={`feature-description-input-${feature.id}`} value={feature.description} onChange={(event) => updateRow(feature.id, "description", event.target.value)} className="border-slate-700 bg-slate-950 text-slate-100" />
                  <select data-testid={`feature-icon-select-${feature.id}`} value={feature.icon} onChange={(event) => updateRow(feature.id, "icon", event.target.value)} className="h-9 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                    {FEATURE_ICON_NAMES.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                  <Input data-testid={`feature-order-input-${feature.id}`} type="number" value={feature.display_order} onChange={(event) => updateRow(feature.id, "display_order", event.target.value)} className="border-slate-700 bg-slate-950 text-slate-100" />
                  <label className="flex items-center gap-2 text-xs text-slate-300">
                    <input data-testid={`feature-active-toggle-${feature.id}`} type="checkbox" checked={Boolean(feature.is_active)} onChange={() => toggleActive(feature)} className="h-4 w-4 accent-emerald-500" />
                    Active
                  </label>
                  <Button data-testid={`feature-save-button-${feature.id}`} size="sm" onClick={() => saveFeature(feature)} disabled={savingId === feature.id} className="bg-blue-500 text-white hover:bg-blue-400">
                    <Save className="h-4 w-4" /> {savingId === feature.id ? "Saving" : "Save"}
                  </Button>
                  <Button data-testid={`feature-delete-button-${feature.id}`} size="sm" variant="outline" aria-label={`Delete ${feature.title}`} onClick={() => deleteFeature(feature)} className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
